import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Package, Plus, MoreVertical, Ban, RotateCcw } from 'lucide-react';
import { STATUS_PILL_CLASS, statusLabel } from './helpers';

/**
 * Phase 1E.1 — App grant list presentation.
 *
 * Mirrors AssignmentList.js structurally but stripped of the AI Employee
 * instance machinery. Apps are toggled on/off per tenant — there's no
 * "deploy N copies" concept like AI Employees have, so no instance
 * count cell, no Provision button, and no max_instances column.
 *
 * Each row shows:
 *   • App cell (icon + name + slug pill)
 *   • Status pill
 *   • Granted by + date
 *   • Kebab menu with:
 *       - Active/suspended → "Revoke grant"
 *       - Revoked          → "Reactivate grant"
 *
 * Props
 *   grants         — Grant[]
 *   appsById       — Map<app_id, AppRegistryDoc>
 *   onRevoke       — (grant, app) => void
 *   onReactivate   — (grant, app) => void
 */
export default function AppGrantList({
  grants,
  appsById,
  onRevoke,
  onReactivate,
}) {
  return (
    <div className="bg-boss-surface border border-boss-border rounded-xl overflow-hidden">
      {/* Desktop table — md+ */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="app-grants-table">
          <thead>
            <tr className="border-b border-boss-border">
              {['App', 'Status', 'Granted by'].map((h) => (
                <th
                  key={h}
                  className="text-left py-3 px-4 text-xs font-medium text-boss-text-muted uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
              <th className="w-20" />
            </tr>
          </thead>
          <tbody>
            {grants.map((g) => {
              const app = appsById.get(g.app_id);
              const isRevoked = g.status === 'revoked';
              return (
                <tr
                  key={g.grant_id}
                  className={
                    'border-b border-boss-border last:border-b-0 transition-colors ' +
                    (isRevoked ? 'opacity-55' : '')
                  }
                  data-testid={`app-grant-row-${g.grant_id}`}
                >
                  <td className="py-3 px-4">
                    <AppCell app={app} fallbackId={g.app_id} />
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex text-xs px-2 py-0.5 rounded-full w-fit ${STATUS_PILL_CLASS[g.status] || ''}`}
                    >
                      {statusLabel(g.status)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-boss-text-secondary text-xs">
                    SimpliMeta
                    <div className="text-[11px] text-boss-text-muted mt-0.5">
                      {isRevoked
                        ? `Revoked ${formatDate(g.revoked_at)}`
                        : formatDate(g.granted_at)}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <KebabMenu
                      grant={g}
                      onRevoke={() => onRevoke(g, app)}
                      onReactivate={() => onReactivate(g, app)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards — below md */}
      <div className="md:hidden p-3 space-y-2.5">
        {grants.map((g) => {
          const app = appsById.get(g.app_id);
          const isRevoked = g.status === 'revoked';
          return (
            <div
              key={g.grant_id}
              className={
                'bg-boss-base border border-boss-border rounded-lg p-3.5 transition ' +
                (isRevoked ? 'opacity-55' : '')
              }
              data-testid={`app-grant-card-${g.grant_id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <AppCell app={app} fallbackId={g.app_id} />
                <span
                  className={`inline-flex text-[10px] px-2 py-0.5 rounded-full shrink-0 ml-auto ${STATUS_PILL_CLASS[g.status] || ''}`}
                >
                  {statusLabel(g.status)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div>
                  <div className="text-[10px] text-boss-text-muted uppercase tracking-wider mb-0.5">
                    Granted by
                  </div>
                  <div className="text-boss-text-secondary">
                    SimpliMeta
                    <div className="text-[10px] text-boss-text-muted mt-0.5">
                      {isRevoked
                        ? `Revoked ${formatDate(g.revoked_at)}`
                        : formatDate(g.granted_at)}
                    </div>
                  </div>
                </div>
                <KebabMenu
                  grant={g}
                  onRevoke={() => onRevoke(g, app)}
                  onReactivate={() => onReactivate(g, app)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Internal: kebab menu ──────────────────────────────────────────

/**
 * Three-dot menu with row-level actions. Manages its own open/closed
 * state plus click-outside dismissal.
 *
 * Menu items by status:
 *   • Active/suspended → "Revoke grant"
 *   • Revoked          → "Reactivate grant"
 */
function KebabMenu({ grant, onRevoke, onReactivate }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // Portalled to document.body, positioned with fixed coords.
  //
  // The previous implementation used `absolute right-0 top-full` inside
  // the row. That works only while every ancestor lets the menu escape,
  // and here none of them do: the list sits in a rounded card with
  // `overflow-hidden`, so a menu opening downward from the last row was
  // clipped at the card edge and the user had to scroll the page to
  // reach items that were rendered but invisible. Portalling removes the
  // menu from that stacking and clipping context entirely.
  //
  // Same approach as `RowKebab` in TenantList.js, which hit this on the
  // "Act as this tenant" entry.

  // Close on outside click, Escape, or any scroll/resize.
  //
  // Because the menu is no longer a DOM descendant of this component, a
  // single wrapper ref would treat clicks on the menu itself as
  // "outside" and dismiss before the item's onClick ran. Both refs are
  // checked.
  //
  // Scroll closes rather than repositions: the anchoring row can leave
  // the viewport, and a fixed menu floating over unrelated rows is worse
  // than one that dismissed. `true` for capture so scrolls inside the
  // table wrapper are caught, not just window scroll.
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleReflow = () => setOpen(false);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleReflow, true);
    window.addEventListener('resize', handleReflow);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleReflow, true);
      window.removeEventListener('resize', handleReflow);
    };
  }, [open]);

  // Position after the menu exists so its real height is measurable.
  // Flip up when there is no room below — the last row of a list is both
  // the common case and exactly where a down-opening menu runs off. This
  // is useLayoutEffect, not useEffect, so the correction lands before
  // paint; the menu renders at opacity 0 until `coords` is set, so there
  // is no flash at the wrong position.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !menuRef.current) return;
    const anchor = buttonRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const GAP = 4;
    const MARGIN = 8;

    const roomBelow = window.innerHeight - anchor.bottom;
    const openUp = roomBelow < menu.height + GAP + MARGIN && anchor.top > menu.height;

    let top = openUp ? anchor.top - menu.height - GAP : anchor.bottom + GAP;
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - menu.height - MARGIN));

    // Right-aligned to the kebab, then clamped so a menu near the left
    // edge (narrow viewport, collapsed sidebar) cannot go off-screen.
    let left = anchor.right - menu.width;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - menu.width - MARGIN));

    setCoords({ top, left });
  }, [open]);

  const isRevoked = grant.status === 'revoked';

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: coords ? coords.top : 0,
        left: coords ? coords.left : 0,
        opacity: coords ? 1 : 0,
      }}
      className="z-[9999] min-w-[180px] bg-boss-surface border border-boss-border rounded-lg shadow-lg py-1"
    >
          {!isRevoked && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onRevoke();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-600/10 transition text-left"
              role="menuitem"
              data-testid="app-grant-kebab-revoke"
            >
              <Ban size={13} />
              Revoke grant
            </button>
          )}
          {isRevoked && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onReactivate();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-600/10 transition text-left"
              role="menuitem"
              data-testid="app-grant-kebab-reactivate"
            >
              <RotateCcw size={13} />
              Reactivate grant
            </button>
          )}
    </div>
  );

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => {
            // Drop stale coords on close so the next open measures fresh
            // rather than flashing at the previous row's position.
            if (v) setCoords(null);
            return !v;
          });
        }}
        className="p-1.5 rounded-md border border-boss-border bg-boss-surface text-boss-text-secondary hover:text-boss-text hover:border-boss-border-hover transition"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`app-grant-kebab-${grant.grant_id}`}
      >
        <MoreVertical size={14} />
      </button>
      {open && createPortal(menu, document.body)}
    </div>
  );
}

// ─── Internal: app cell renderer ────────────────────────────────────

function AppCell({ app, fallbackId }) {
  if (!app) {
    return (
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-md bg-boss-elevated flex items-center justify-center shrink-0">
          <Package size={16} className="text-boss-text-muted" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-boss-text-muted truncate">
            Unknown app
          </div>
          <div className="text-[11px] text-boss-text-muted font-mono mt-0.5 truncate">
            {fallbackId}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 bg-blue-100 dark:bg-blue-600/20 text-blue-700 dark:text-blue-300">
        <Package size={16} />
      </div>
      <div className="min-w-0">
        <div className="font-medium text-boss-text truncate">{app.name}</div>
        <div className="mt-0.5">
          <span className="inline-flex text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-boss-elevated text-boss-text-muted font-mono">
            {app.slug}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return dateFmt.format(d);
}
